import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getMasterUnit,
  shipMasterUnit,
  printMasterUnit,
  MasterUnit,
  ShipMasterUnitRequest
} from '../../services/masterUnitApi';
import { useTheme } from '../../contexts/ThemeContext';
import useToast from '../../hooks/useToast';
import Toast from '../../components/Toast';
import GenericButton from '../../components/GenericButton';
import GenericModal from '../../components/GenericModal';
import StatusBadge from '../../components/StatusBadge';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  Stack,
  Divider,
  TextField,
  Chip,
  Link as MuiLink
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import { ConsignmentView } from '../Consignments';

const MasterUnitView: React.FC = () => {
  const { theme } = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast, showSuccess, showError, hideToast } = useToast();

  const [masterUnit, setMasterUnit] = useState<MasterUnit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showShipModal, setShowShipModal] = useState(false);
  const [shipLoading, setShipLoading] = useState(false);
  const [consignmentModalOpen, setConsignmentModalOpen] = useState(false);
  const [selectedConsignmentId, setSelectedConsignmentId] = useState<number | null>(null);

  const [shipData, setShipData] = useState<ShipMasterUnitRequest>({
    vehicle_no: '',
    contact_no: '',
    company_name: '',
    eta: '',
    attachment: new File([], '')
  });

  useEffect(() => {
    if (id) {
      fetchMasterUnit();
    }
  }, [id]);

  const fetchMasterUnit = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getMasterUnit(Number(id));
      setMasterUnit(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleShip = async () => {
    if (!masterUnit) return;

    try {
      setShipLoading(true);
      await shipMasterUnit(masterUnit.id, shipData);
      setShowShipModal(false);
      showSuccess('Master unit shipped successfully!');
      fetchMasterUnit();
    } catch (err: any) {
      showError(err.message || 'Failed to ship master unit');
    } finally {
      setShipLoading(false);
    }
  };

  const handlePrint = () => {
    if (masterUnit) {
      printMasterUnit(masterUnit.id);
    }
  };

  const handlePrintAWB = () => {
    if (masterUnit) {
      printMasterUnit(masterUnit.id, true);
    }
  };

  const handleViewConsignment = (consignmentId: number) => {
    setSelectedConsignmentId(consignmentId);
    setConsignmentModalOpen(true);
  };

  const handleCloseConsignmentModal = () => {
    setConsignmentModalOpen(false);
    setSelectedConsignmentId(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 256 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !masterUnit) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" color="error" sx={{ mb: 2 }}>Error</Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>{error}</Typography>
          <GenericButton onClick={() => navigate('/master-units')}>Back to Master Units</GenericButton>
        </Box>
      </Box>
    );
  }

  if (!masterUnit) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 2 }}>Master Unit Not Found</Typography>
          <GenericButton onClick={() => navigate('/master-units')}>Back to Master Units</GenericButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Master Unit Details</Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mt: 0.5 }}>MU Number: {masterUnit.mu_number}</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {masterUnit.status_id === 5 && (
            <GenericButton onClick={() => setShowShipModal(true)}>Mark as Shipped</GenericButton>
          )}
          <GenericButton variant="secondary" onClick={handlePrint}>
            <PrintIcon sx={{ mr: 1 }} /> Print
          </GenericButton>
          {masterUnit.status_id === 6 && (
            <GenericButton variant="secondary" onClick={handlePrintAWB}>
              <PrintIcon sx={{ mr: 1 }} /> Print AWB
            </GenericButton>
          )}
          <GenericButton variant="outline" onClick={() => navigate('/master-units')}>Back to List</GenericButton>
        </Stack>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {/* Basic Info */}
      <Card sx={{ mb: 3, bgcolor: theme.palette.background.paper }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Basic Information</Typography>
          <Grid container spacing={2}>
            {masterUnit.journey_type !== 'Failed Delivery' ? (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" color="textSecondary" display="block">Branch</Typography>
                <Typography variant="body1">{masterUnit.inboundBranch?.name || 'N/A'}</Typography>
              </Grid>
            ) : (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" color="textSecondary" display="block">Customer Name</Typography>
                <Typography variant="body1">{masterUnit.client?.name || 'N/A'}</Typography>
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="textSecondary" display="block">Created Date</Typography>
              <Typography variant="body1">{new Date(masterUnit.created_at).toLocaleDateString()}</Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="textSecondary" display="block">Status</Typography>
              <StatusBadge status={{ id: masterUnit.status_id, name: masterUnit.status?.name || 'Unknown' }} />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="textSecondary" display="block">Total Consignments</Typography>
              <Typography variant="body1">{masterUnit.consignments?.length || 0}</Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="textSecondary" display="block">Handover Count</Typography>
              <Typography variant="body1">{masterUnit.consignments?.filter(c => c.statusInMasterUnit === 'Hand Over').length || 0}</Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="textSecondary" display="block">Seal Number</Typography>
              <Typography variant="body1">{masterUnit.seal_number}</Typography>
            </Grid>

            {masterUnit.journey_type !== 'Failed Delivery' && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" color="textSecondary" display="block">Destination</Typography>
                <Typography variant="body1">{masterUnit.destinationCity?.name || 'N/A'}</Typography>
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="textSecondary" display="block">Journey Type</Typography>
              <Typography variant="body1">{masterUnit.journey_type}</Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="textSecondary" display="block">MU Type</Typography>
              <Typography variant="body1">{masterUnit.mu_type}</Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="textSecondary" display="block">Type of Shipment</Typography>
              <Typography variant="body1">{masterUnit.type}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Consignments */}
      <Card sx={{ bgcolor: theme.palette.background.paper }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">Consignments ({masterUnit.consignments?.length || 0})</Typography>
            <Stack direction="row" spacing={1}>
              <IconButton size="small" onClick={handlePrint}><PrintIcon /></IconButton>
            </Stack>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Consignment #</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Service Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Booking Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Booking Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Total/COD Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Short Received</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status in MU</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {masterUnit.consignments && masterUnit.consignments.length > 0 ? (
                  masterUnit.consignments.map((consignment) => (
                    <TableRow key={consignment.id}>
                      <TableCell>
                        <MuiLink
                          component="button"
                          onClick={() => handleViewConsignment(consignment.id)}
                          sx={{ textDecoration: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                          {consignment.consignment_no}
                        </MuiLink>
                      </TableCell>
                      <TableCell>{consignment.service_type}</TableCell>
                      <TableCell>{consignment.booking_type}</TableCell>
                      <TableCell>{new Date(consignment.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>{consignment.booking_type === 'COD' ? consignment.cod_amount : consignment.total_price}</TableCell>
                      <TableCell>
                        {consignment.statusInMasterUnit === 'Short Received' ? (
                          <Typography variant="body2">{consignment.reasonInMasterUnit}</Typography>
                        ) : (
                          <Typography variant="body2" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={{ id: 0, name: consignment.statusInMasterUnit || 'Unknown' }} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="textSecondary">No consignments found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Ship Modal */}
      <GenericModal open={showShipModal} onClose={() => setShowShipModal(false)} title="Enter Shipping Details">
        <Box component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); handleShip(); }} sx={{ display: 'grid', gap: 2 }}>
          <TextField
            label="Vehicle No."
            value={shipData.vehicle_no}
            onChange={(e) => setShipData(prev => ({ ...prev, vehicle_no: e.target.value }))}
            fullWidth
            required
          />
          <TextField
            label="Contact No."
            value={shipData.contact_no}
            onChange={(e) => setShipData(prev => ({ ...prev, contact_no: e.target.value }))}
            fullWidth
            required
          />
          <TextField
            label="3PL Company Name"
            value={shipData.company_name}
            onChange={(e) => setShipData(prev => ({ ...prev, company_name: e.target.value }))}
            fullWidth
            required
          />
          <TextField
            label="Estimated Time of Arrival"
            type="time"
            value={shipData.eta}
            onChange={(e) => setShipData(prev => ({ ...prev, eta: e.target.value }))}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />
          <input
            type="file"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                setShipData(prev => ({ ...prev, attachment: e.target.files![0] }));
              }
            }}
            accept=".pdf,.jpg,.jpeg,.png"
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
            <GenericButton variant="outline" type="button" onClick={() => setShowShipModal(false)}>Cancel</GenericButton>
            <GenericButton type="submit" disabled={shipLoading}>{shipLoading ? 'Shipping...' : 'Submit'}</GenericButton>
          </Stack>
        </Box>
      </GenericModal>

      {/* Consignment Details Modal */}
      <ConsignmentView
        open={consignmentModalOpen}
        onClose={handleCloseConsignmentModal}
        consignmentId={selectedConsignmentId}
      />

      {/* Toast Notification */}
      <Toast
        open={toast.open}
        message={toast.message}
        title={toast.title}
        severity={toast.severity}
        onClose={hideToast}
      />
    </Box>
  );
};

export default MasterUnitView;
